import React, { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import moment from "moment/moment";

const ReceiveReturnableItems = () => {
  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");
  const name = localStorage.getItem("name");
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [voucherItems, setVoucherItems] = useState([]); // ----------------------------------
  // FETCH LEDGERS
  // ----------------------------------
  const fetchledgermasterdetails = async () => {
    try {
      const response = await apiClient.get(SERVER_BASE + "/ledgerMasters", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const options = response.data.map((ledger) => ({
        value: ledger.printName,
        label: ledger.printName,
        ledgerId: ledger.id,
      }));

      setLedgerOptions(options);
    } catch (error) {
      console.error("Error fetching ledger master details:", error);
    }
  };

  // ----------------------------------
  // FETCH VOUCHERS
  // ----------------------------------
  const fetchVoucherOptions = async (ledgerId) => {
    try {
      const response = await apiClient.get(
        SERVER_BASE + `/getReturnableDeliveryChallans?ledgerId=${ledgerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const options = response.data.map((challan) => ({
        value: challan.id,
        label: `${challan.prefix || ""}${challan.refNo || ""} - ${moment(challan.date).format('DD-MMM-YY') || ""}`,
        challan,
      }));

      setVoucherOptions(options);
    } catch (error) {
      console.error("Error fetching returnable challans:", error);
    }
  };

  // ----------------------------------
  // LEDGER CHANGE
  // ----------------------------------
  const handleLedgerChange = (selected) => {
    setSelectedLedger(selected);

    setSelectedVoucher(null);
    setVoucherOptions([]);

    if (selected?.ledgerId) {
      fetchVoucherOptions(selected.ledgerId);
    }
  };

  // ----------------------------------
  // VOUCHER CHANGE
  // ----------------------------------
  const handleVoucherChange = (selected) => {
    setSelectedVoucher(selected);

    if (!selected?.challan?.items) {
      setVoucherItems([]);
      return;
    }

    const savedItems = selected?.challan?.receivingData?.receivedItems || [];

    const items = selected.challan.items.map((item) => {
      const departments = [
        ...new Set(
          (item.locationDetails || []).map((x) => x.department).filter(Boolean),
        ),
      ];

      const departmentOptions = departments.map((dept) => ({
        value: dept,
        label: dept,
      }));

      const savedItem = savedItems.find(
        (x) => String(x.itemId) === String(item.itemId),
      );

      let receivingRows = [];

      if (savedItem && savedItem.receives && savedItem.receives.length > 0) {
        receivingRows = savedItem.receives.map((receive) => ({
          date: receive.date || "",
          name: receive.name || "",
          mobile: receive.mobile || "",
          vehicleNo: receive.vehicleNo || "",
          department:
            departmentOptions.find((d) => d.value === receive.department) ||
            null,
          qty: receive.qty || "",
        }));
      } else {
        receivingRows = [
          {
            date: new Date().toISOString().split("T")[0],
            name: "",
            mobile: "",
            vehicleNo: "",
            department:
              departmentOptions.length === 1 ? departmentOptions[0] : null,
            qty: "",
          },
        ];
      }

      return {
        ...item,
        departmentOptions,
        receivingRows,
      };
    });

    setVoucherItems(items);
  };
  const addReceivingRow = (itemIndex) => {
    const updated = [...voucherItems];

    updated[itemIndex].receivingRows.push({
      date: new Date().toISOString().split("T")[0],
      name: "",
      mobile: "",
      vehicleNo: "",
      department:
        updated[itemIndex].departmentOptions.length === 1
          ? updated[itemIndex].departmentOptions[0]
          : null,
      qty: "",
    });

    setVoucherItems(updated);
  };

  const removeReceivingRow = (itemIndex, rowIndex) => {
    const updated = [...voucherItems];

    if (updated[itemIndex].receivingRows.length > 1) {
      updated[itemIndex].receivingRows.splice(rowIndex, 1);
      setVoucherItems(updated);
    }
  };

  const updateReceivingRow = (itemIndex, rowIndex, field, value) => {
    const updated = [...voucherItems];

    updated[itemIndex].receivingRows[rowIndex][field] = value;

    setVoucherItems(updated);
  };

  // ----------------------------------
  // INITIAL LOAD
  // ----------------------------------
  useEffect(() => {
    fetchledgermasterdetails();
  }, []);
  const handleSave = async () => {
    try {
      if (!selectedVoucher) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Please select a voucher",
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      const payloadItems = [];
      let hasAnyReceiveQty = false;

      for (const item of voucherItems) {
        let totalReceiveQty = 0;
        const validReceives = [];

        for (const row of item.receivingRows) {
          const qty = Number(row.qty || 0);

          // Skip empty rows
          if (qty <= 0) {
            continue;
          }

          hasAnyReceiveQty = true;

          if (!row.date) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Date is required for ${item.itemName}`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (!row.name?.trim()) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Name is required for ${item.itemName}`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (!row.mobile?.trim()) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Mobile is required for ${item.itemName}`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          if (!row.department) {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: `Department is required for ${item.itemName}`,
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }

          totalReceiveQty += qty;

          validReceives.push({
            date: row.date,
            name: row.name.trim(),
            mobile: row.mobile.trim(),
            vehicleNo: row.vehicleNo?.trim() || "",
            department: row.department?.value,
            qty,
          });
        }

        if (totalReceiveQty > Number(item.quantity)) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: `${item.itemName} receive qty cannot exceed ${item.quantity}`,
            showConfirmButton: false,
            timer: 4000,
          });
          return;
        }

        // Only include items that actually have received qty
        if (validReceives.length > 0) {
          payloadItems.push({
            itemId: item.itemId || item.id,
            itemName: item.itemName,
            uom: item.uom,
            issuedQty: Number(item.quantity),
            totalReceiveQty,
            receives: validReceives,
          });
        }
      }

      if (!hasAnyReceiveQty) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Please enter receive quantity for at least one item",
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      const payload = {
        challanId: selectedVoucher.value,
        voucherNo: selectedVoucher?.challan
          ? `${selectedVoucher.challan.prefix || ""}${
              selectedVoucher.challan.refNo || ""
            }`
          : "",
        ledgerId: selectedLedger?.ledgerId,
        ledgerName: selectedLedger?.label,
        items: payloadItems,
        createdBy: name,
        centerCode: code,
      };

      const response = await apiClient.post(
        SERVER_BASE + "/saveReturnedItems",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: response?.data?.message || "Returned items saved successfully",
        showConfirmButton: false,
        timer: 3000,
      });
      // Optional reset
      // setSelectedVoucher(null);
      // setVoucherItems([]);
    } catch (error) {
      console.error(error);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title:
          error?.response?.data?.message || "Failed to save returned items",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };
  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-1">Receive Returnable Items</h4>

      <div className="row">
        {/* Ledger */}
        <div className="col-md-6 mb-3">
          <label className="form-label">Ledger</label>

          <Select
            options={ledgerOptions}
            value={selectedLedger}
            onChange={handleLedgerChange}
            placeholder="Select Ledger"
            isClearable
          />
        </div>

        {/* Voucher */}
        <div className="col-md-5 mb-3">
          <label className="form-label">Voucher No</label>

          <Select
            options={voucherOptions}
            value={selectedVoucher}
            onChange={handleVoucherChange}
            placeholder="Select Voucher"
            isDisabled={!selectedLedger}
            isClearable
          />
        </div>

        <div className="col-md-1 mb-3 d-flex align-items-end">
          <button
            className="btn btn-success w-100"
            onClick={handleSave}
            disabled={!selectedVoucher}
          >
            Save
          </button>
        </div>
      </div>
      {/* Next section */}
      {selectedVoucher && (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle">
              <thead>
                <tr>
                  <th>Item</th>
                  <th width="100">Sent Qty</th>
                  <th width="120">Receive Qty</th>
                  <th width="140">Received For</th>
                  <th>Deposited By</th>
                  <th width="140">Vehicle No</th>
                  <th width="140">Mobile No</th>
                  <th width="220">Receive Date</th>
                  <th width="90">Action</th>
                </tr>
              </thead>

              <tbody>
                {voucherItems.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    {item.receivingRows.map((row, rowIndex) => (
                      <tr key={`${itemIndex}-${rowIndex}`}>
                        {rowIndex === 0 && (
                          <>
                            <td
                              rowSpan={item.receivingRows.length}
                              className="fw-bold align-middle"
                            >
                              {item.itemName}
                            </td>

                            <td
                              rowSpan={item.receivingRows.length}
                              className="align-middle"
                            >
                              {item.quantity}
                            </td>
                          </>
                        )}
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.qty}
                            onChange={(e) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "qty",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td style={{ minWidth: "220px" }}>
                          <Select
                            value={row.department}
                            options={item.departmentOptions}
                            onChange={(value) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "department",
                                value,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.vehicleNo}
                            onChange={(e) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "vehicleNo",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.name}
                            onChange={(e) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "name",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.mobile}
                            onChange={(e) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "mobile",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={row.date}
                            onChange={(e) =>
                              updateReceivingRow(
                                itemIndex,
                                rowIndex,
                                "date",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                removeReceivingRow(itemIndex, rowIndex)
                              }
                              disabled={item.receivingRows.length === 1}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>

                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => addReceivingRow(itemIndex)}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiveReturnableItems;
